import DateTimeFormatter from '@Components/DateTimeFormatter/DateTimeFormatter';
import MainTable from '@Components/MainTable';
import { MainTableRow } from '@Components/MainTable/MainTable';
import { ActionsTableRow } from '@Components/MainTable/MainTable.styled';
import { onPageChangeRequestMeta } from '@Components/MainTable/MainTable.types';
import NoResults from '@Components/NoResults/NoResults';
import RoundButton from '@Components/UI/Buttons/RoundButton';
import EdConfirmModal from '@Components/UI/Modals/EdConfirmModal/EdConfirmModal';
import EdActiveStatus from '@Components/UI/Utilities/EdActiveStatus/EdActiveStatus';
import EdEmptyValue from '@Components/UI/Utilities/EdEmptyValue/EdEmptyValue';
import EdIcon from '@Components/UI/Utilities/EdIcon/EdIcon';
import EdSpeedDial, {
	EdSpeedDialAction,
} from '@Components/UI/Utilities/EdSpeedDial/EdSpeedDial';
import { useTypedSelector } from '@Features/store';
import { TableCell, Tooltip } from '@mui/material';
import { useSnackbar } from '@Providers/useSnackbar';
import { FlexLayout } from '@Styled/utilities';
import { useCallback, useState } from 'react';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import GenerateInstructorCodes from '../../components/GenerateInstructorCodes';
import {
	instructorCodesDeleteById,
	instructorCodesExportData,
	instructorCodesGetAll,
} from '../../Slices/InstructorCodes.slice';
import { InstructorCode } from '../../Types/InstructorCodes.types';

const TABLE_HEADS: Array<TableHeader> = [
	{ displayName: 'CODE', fieldName: 'code', canSort: true },
	{ displayName: 'BATCH', fieldName: 'batch', canSort: true },
	{ displayName: 'INSTRUCTOR', fieldName: 'instructor', canSort: true },
	{ displayName: 'GROUP', fieldName: 'group_name', canSort: true },
	{ displayName: 'USED', fieldName: 'student_id' },
	{ displayName: 'USED AT', fieldName: 'updated_at', width: '12%' },
	{ displayName: 'USERNAME', fieldName: 'username', width: '10%', canSort: true },
	{ displayName: 'PHONE NUMBER', fieldName: 'phone_number', width: '10%' },
	{ displayName: 'CREATED AT', fieldName: 'created_at', width: '12%', canSort: true },
];

const InstructorCodesTable = () => {
	const dispatch = useDispatch();
	const onPageRequestChange = useCallback(
		({
			activePage,
			endDate,
			filters,
			query,
			sortBy,
			startDate,
			perPage,
			export: exportData,
		}: onPageChangeRequestMeta) => {
			dispatch(
				instructorCodesGetAll({
					page: activePage,
					filters: filters,
					perPage: perPage ?? 10,
					query,
					export: exportData ?? false,
					sortBy: sortBy,
					from: startDate,
					to: endDate,
				})
			);
		},
		[dispatch]
	);

	const { fetchAll, filters, meta, selectedFilters, dateRange, query, sortBy } =
		useTypedSelector((state) => state.InstructorCodes);
	const auth = useTypedSelector((state) => state.auth);

	const [openGenerateModel, setOpenGenerateModel] = useState(false);
	const [toBeDeleted, setToBeDelete] = useState<number | undefined>();
	const [openConfirmModal, setOpenConfirmModal] = useState(false);
	const { displaySnackbar } = useSnackbar();

	const handleDeleteClick = (id?: number) => {
		setOpenConfirmModal(true);
		setToBeDelete(id);
	};

	const handleCloseDeleteModal = (close: boolean, confirm?: boolean) => {
		setOpenConfirmModal(close);
		if (confirm && toBeDeleted) {
			try {
				dispatch(instructorCodesDeleteById(toBeDeleted));
				displaySnackbar('success', 'Unlock center code deleted successfully');
			} catch (error) {}
		}
		setToBeDelete(undefined);
	};

	const handleCopy = async (item: InstructorCode) => {
		await navigator.clipboard.writeText(item.code);
		displaySnackbar('success', 'Code copied to clipboard');
	};

	return (
		<div>
			<GenerateInstructorCodes
				openModel={openGenerateModel}
				handleModelClose={() => setOpenGenerateModel(false)}
			/>
			<EdConfirmModal
				open={openConfirmModal}
				handleClose={handleCloseDeleteModal}
				text={'Are you sure to delete this unlock center code ?'}
			/>
			<FlexLayout justifyContent="flex-end">
				{(auth.user.type === 'super' || auth.permissions.has('v-instc')) && (
					<RoundButton
						onClick={() => {
							dispatch(
								instructorCodesExportData({
									filters: selectedFilters,
									page: meta?.current_page ?? 1,
									perPage: meta?.per_page ?? 10,
									query: query ?? '',
									sortBy,
									from: dateRange.from,
									to: dateRange.to,
								})
							);
						}}
						sx={{ marginRight: '1rem' }}
						startIcon={<EdIcon>south</EdIcon>}
						variant="contained"
					>
						Export
					</RoundButton>
				)}
				{(auth.user.type === 'super' || auth.permissions.has('gen-instc')) && (
					<RoundButton
						edcolor="purple"
						startIcon={<EdIcon>add</EdIcon>}
						variant="contained"
						onClick={() => setOpenGenerateModel(true)}
					>
						Generate codes
					</RoundButton>
				)}
			</FlexLayout>
			<CompactHeaders>
			<MainTable
				tableFilters={filters}
				tableHeads={TABLE_HEADS}
				totalPages={meta?.last_page ?? 0}
				onPageChangeRequest={onPageRequestChange}
				RowsperPage={meta?.per_page}
				$hasActions
				pageNum={meta?.current_page}
				total={meta?.total}
				renderItems={() => {
					if (fetchAll.length === 0) {
						return (
							<MainTableRow>
								<TableCell colSpan={TABLE_HEADS.length}>
									<NoResults />
								</TableCell>
							</MainTableRow>
						);
					} else {
						return fetchAll.map((code) => {
							return (
								<MainTableRow key={code.id}>
									<Tooltip title={code.code} placement="top">
										<TableCell>{code.code}</TableCell>
									</Tooltip>
									<TableCell>{code.batch ?? '-'}</TableCell>
									<Tooltip
										title={code.instructor_label ?? ''}
										placement="top"
									>
										<TableCell>{code.instructor_label ?? '-'}</TableCell>
									</Tooltip>
									<Tooltip title={code.group_name ?? ''} placement="top">
										<TableCell>{code.group_name ?? '-'}</TableCell>
									</Tooltip>
									<TableCell>
										<EdActiveStatus active={code.student_id !== null} />
									</TableCell>
									{/* Empty title = no tooltip: an unused row's updated_at is
									    just its import/generation time, not a used-at. */}
									<Tooltip
										title={
											code.student_id !== null ? (
												<DateTimeFormatter date={code.updated_at} />
											) : (
												''
											)
										}
										placement="top"
									>
										<TableCell>
											{code.student_id !== null ? (
												<DateTimeFormatter date={code.updated_at} />
											) : (
												'-'
											)}
										</TableCell>
									</Tooltip>
									<TableCell>
										<EdEmptyValue value={code.username} />
									</TableCell>
									<TableCell>
										<EdEmptyValue value={code.phone_number} />
									</TableCell>
									<Tooltip
										title={<DateTimeFormatter date={code.created_at} />}
										placement="top"
									>
										<TableCell>
											<DateTimeFormatter date={code.created_at} />
										</TableCell>
									</Tooltip>
									<ActionsTableRow>
										{(auth.user.type === 'super' ||
											auth.permissions.has('d-instc')) && (
											<EdSpeedDial ariaLabel="Actions">
												{[
													// A used code is what keeps the student's center
													// classrooms visible — the backend refuses to
													// delete it, so don't offer to.
													code.student_id === null && (
														<EdSpeedDialAction
															onClick={() => handleDeleteClick(code.id)}
															key="delete"
															tooltipTitle="Delete"
															icon={<EdIcon>delete</EdIcon>}
															aria-label=""
														/>
													),
													<EdSpeedDialAction
														onClick={() => handleCopy(code)}
														key="copy"
														tooltipTitle="Copy"
														icon={<EdIcon>content_copy</EdIcon>}
														aria-label=""
													/>,
												].filter(Boolean)}
											</EdSpeedDial>
										)}
									</ActionsTableRow>
								</MainTableRow>
							);
						});
					}
				}}
			/>
			</CompactHeaders>
		</div>
	);
};

export default InstructorCodesTable;

/* Same compact-header treatment as the scratchcards table — nine columns of
   1rem nowrap uppercase headers collide at laptop widths. */
const CompactHeaders = styled.div`
	th.MuiTableCell-head {
		font-size: 0.78rem;
		letter-spacing: 0.03em;
		padding-left: 0.4rem;
		padding-right: 0.4rem;
	}

	td.MuiTableCell-root {
		padding-left: 0.4rem;
		padding-right: 0.4rem;
	}
`;
